import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO.2 — Shared per-route SEO component.
 *
 * Wire into any public page:
 *   <SEO title="..." description="..." path="/foo" />
 *
 * Emits:
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical">
 *   - Open Graph tags (og:title, og:description, og:url, og:type, og:image)
 *   - Twitter Card tags
 *   - Optional JSON-LD structured data via `jsonLd` prop (Article, Product, etc.)
 *   - Optional <meta name="robots"> when noIndex is true
 */
const DEFAULT_SITE_NAME = '30 Day Skills';
const DEFAULT_TITLE = '30 Day Skills — Leadership books & AI Companion for IT managers';
const DEFAULT_DESCRIPTION =
  'Practical leadership training for IT managers. Read the 30-Day Skills book series, use the AI Companion for daily reflection, and grow a real Living CV of demonstrated capability.';
const DEFAULT_OG_IMAGE = '/og-default.png';

// Detect at build time; falls back to window at runtime so client-side nav still works.
const getSiteUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_PUBLIC_URL || 'https://30dayskills.co.uk';
};

const SEO = ({
  title,
  description,
  path,
  image,
  type = 'website',
  noIndex = false,
  jsonLd = null,
  siteName = DEFAULT_SITE_NAME,
}) => {
  const siteUrl = getSiteUrl();
  const canonicalPath = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonical = `${siteUrl}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;

  const finalTitle = title
    ? title.length > 60 ? title.slice(0, 57) + '…' : title
    : DEFAULT_TITLE;

  const finalDescription = description
    ? description.length > 160 ? description.slice(0, 157) + '…' : description
    : DEFAULT_DESCRIPTION;

  const ogImage = image
    ? (image.startsWith('http') ? image : `${siteUrl}${image}`)
    : `${siteUrl}${DEFAULT_OG_IMAGE}`;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
